import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, goToLogin } from "../api/client";
import MainLayout from "../layout/MainLayout";
import { message } from "antd";

const StatusBadge = ({ status }) => {
  const colors = {
    PENDING: { bg: "rgba(247,166,79,0.15)", color: "#F7A64F" },
    COMPLETED: { bg: "rgba(27,187,107,0.12)", color: "#1bbb6b" },
    FAILED: { bg: "rgba(255,80,80,0.12)", color: "#ff5050" },
  };
  const s = colors[status] || colors.PENDING;
  return (
    <span
      className="px-2 py-1 rounded-pill fw-semibold"
      style={{ fontSize: "11px", background: s.bg, color: s.color }}
    >
      {status}
    </span>
  );
};

const StatCard = ({ label, value, color }) => (
  <div className="border rounded-3 p-4 d-flex flex-column gap-1" style={{ flex: 1 }}>
    <span style={{ color: "var(--text-muted)", fontSize: "13px" }}>{label}</span>
    <span className="fw-bold fs-3" style={{ color: color || "inherit" }}>
      {value ?? "-"}
    </span>
  </div>
);

const AdminPanel = () => {
  const [deposits, setDeposits] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [depositsRes, statsRes] = await Promise.all([
        apiClient.get("/admin/deposits/pending"),
        apiClient.get("/admin/stats"),
      ]);
      setDeposits(depositsRes.data.deposits || []);
      setStats(statsRes.data.stats || null);
    } catch (err) {
      if (err.response?.status === 401) {
        goToLogin();
      } else if (err.response?.status === 403) {
        setForbidden(true);
      } else {
        message.error("Failed to load admin data");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = async (transferId, reference) => {
    setApproving(transferId);
    try {
      const res = await apiClient.post(`/admin/deposits/${transferId}/approve`);
      message.success(`✅ ${res.data.message}`);
      fetchData(); // refresh list
    } catch (err) {
      message.error(err.response?.data?.message || "Approval failed");
    } finally {
      setApproving(null);
    }
  };

  if (forbidden) {
    return (
      <MainLayout expand={false} NavName="Admin Panel">
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "60vh", gap: "12px" }}>
          <span style={{ fontSize: "48px" }}>🔒</span>
          <h2 className="fw-bold">Access Denied</h2>
          <p style={{ color: "var(--text-muted)" }}>
            Your account is not authorised to access the admin panel.
          </p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout expand={false} NavName="Admin Panel">
      <div className="container mt-5" style={{ maxWidth: "1100px" }}>
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h1 className="fs-3 fw-bold m-0">Admin Panel</h1>
            <span style={{ color: "var(--text-muted)", fontSize: "13px" }}>
              Manage pending deposit approvals
            </span>
          </div>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={fetchData}
            disabled={loading}
          >
            {loading ? "Refreshing…" : "↻ Refresh"}
          </button>
          <button
            className="btn btn-sm"
            style={{
              background: "linear-gradient(135deg, #0d3b2e, #1bbb6b)",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              padding: "6px 16px",
              fontWeight: 600,
              fontSize: "12px",
            }}
            onClick={() => navigate("/admin/transactions")}
          >
            📋 Transaction History
          </button>
        </div>

        {/* Stats Row */}
        {stats && (
          <div className="d-flex gap-3 mb-4 flex-wrap">
            <StatCard label="Total Users" value={stats.totalUsers} />
            <StatCard
              label="Pending Deposits"
              value={stats.pendingDeposits}
              color={stats.pendingDeposits > 0 ? "#F7A64F" : undefined}
            />
            <StatCard label="Approved Deposits" value={stats.totalDeposits} color="#1bbb6b" />
          </div>
        )}

        {/* Pending Deposits Table */}
        <div className="border rounded-3 overflow-hidden">
          <div className="p-4 border-bottom d-flex align-items-center justify-content-between">
            <h2 className="fs-5 fw-semibold m-0">Pending Deposit Requests</h2>
            <span
              className="px-2 py-1 rounded-pill fw-semibold"
              style={{ fontSize: "12px", background: "rgba(247,166,79,0.15)", color: "#F7A64F" }}
            >
              {deposits.length} pending
            </span>
          </div>

          {loading ? (
            <div className="p-5 text-center" style={{ color: "var(--text-muted)" }}>
              Loading…
            </div>
          ) : deposits.length === 0 ? (
            <div className="p-5 text-center" style={{ color: "var(--text-muted)" }}>
              <span style={{ fontSize: "32px" }}>✅</span>
              <p className="mt-2 mb-0">No pending deposits, all clear!</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table mb-0" style={{ fontSize: "13px" }}>
                <thead>
                  <tr style={{ background: "var(--card-bg)" }}>
                    <th className="px-4 py-3 fw-semibold border-bottom">Reference</th>
                    <th className="px-4 py-3 fw-semibold border-bottom">User</th>
                    <th className="px-4 py-3 fw-semibold border-bottom">Amount</th>
                    <th className="px-4 py-3 fw-semibold border-bottom">Note</th>
                    <th className="px-4 py-3 fw-semibold border-bottom">Date</th>
                    <th className="px-4 py-3 fw-semibold border-bottom">Status</th>
                    <th className="px-4 py-3 fw-semibold border-bottom text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {deposits.map((dep) => (
                    <tr key={dep.id} style={{ borderBottom: "1px solid var(--border-color)" }}>
                      <td className="px-4 py-3">
                        <code style={{ fontSize: "12px", color: "var(--accent-color)" }}>
                          {dep.reference}
                        </code>
                      </td>
                      <td className="px-4 py-3">
                        <div className="fw-semibold">{dep.user?.name || "-"}</div>
                        <div style={{ color: "var(--text-muted)", fontSize: "11px" }}>
                          {dep.user?.email}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="fw-bold">{dep.amount?.toLocaleString()}</span>{" "}
                        <span style={{ color: "var(--text-muted)" }}>{dep.currency}</span>
                      </td>
                      <td className="px-4 py-3" style={{ color: "var(--text-muted)" }}>
                        {dep.referenceNote || "-"}
                      </td>
                      <td className="px-4 py-3" style={{ color: "var(--text-muted)" }}>
                        {dep.createdAt
                          ? new Date(dep.createdAt).toLocaleString("en-AU", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "-"}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={dep.status} />
                      </td>
                      <td className="px-4 py-3 text-end">
                        <button
                          className="btn btn-sm"
                          style={{
                            background: "#1bbb6b",
                            color: "#fff",
                            border: "none",
                            borderRadius: "8px",
                            padding: "6px 14px",
                            fontWeight: 600,
                            fontSize: "12px",
                            opacity: approving === dep.id ? 0.7 : 1,
                          }}
                          disabled={approving === dep.id}
                          onClick={() => handleApprove(dep.id, dep.reference)}
                        >
                          {approving === dep.id ? "Approving…" : "Approve ✓"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default AdminPanel;
